import React, { useCallback, useEffect, useState } from 'react';
import { financeApi, usersApi, clientsApi } from '../services/api';
import { toast } from 'react-hot-toast';
import { ArrowPathIcon, ChevronLeftIcon, ChevronRightIcon, TrashIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '../components/ui/table';

// ── helpers ──────────────────────────────────────────────────────────────────
const CUR_SYMBOL: Record<string, string> = {
  RUB: '₽', KZT: '₸', USD: '$', UZS: 'сўм', KGS: '⃀', BYN: 'Br', AMD: '֏', GEL: '₾',
};
const cur = (c?: string | null) => (c ? (CUR_SYMBOL[c] || c) : '—');
const fmt = (n: number) => (Number(n) || 0).toLocaleString('ru-RU', { maximumFractionDigits: 2 });

const KIND_LABEL: Record<string, string> = {
  settlement: 'Расчётный счёт', owner: 'Владельцу', employee: 'Сотруднику', cash: 'Наличные', other: 'Прочее',
};
const CATEGORY_LABEL: Record<string, string> = {
  rent: 'Аренда', services: 'Сервисы', taxes: 'Налоги', equipment: 'Оборудование', other: 'Прочее',
};
const CURRENCIES = ['RUB', 'KZT', 'USD', 'UZS', 'KGS', 'BYN', 'AMD', 'GEL'];

function currentMonthStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function shiftMonth(ym: string, delta: number): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, (m - 1) + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  const names = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  return `${names[(m - 1) % 12]} ${y}`;
}

type Tab = 'summary' | 'payments' | 'ggboot' | 'payroll' | 'expenses' | 'accounts' | 'clients' | 'analytics';

const TABS: { key: Tab; label: string }[] = [
  { key: 'summary', label: 'Сводка' },
  { key: 'payments', label: 'Оплаты клиентов' },
  { key: 'ggboot', label: 'GGBoot' },
  { key: 'payroll', label: 'Зарплаты' },
  { key: 'expenses', label: 'Расходы' },
  { key: 'accounts', label: 'Счета' },
  { key: 'analytics', label: 'Аналитика' },
];

const selectCls =
  'h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground';

const FinancePage: React.FC = () => {
  const [period, setPeriod] = useState<string>(currentMonthStr());
  const [tab, setTab] = useState<Tab>('summary');
  const [loading, setLoading] = useState(false);

  const [summary, setSummary] = useState<Record<string, any>>({});
  const [payments, setPayments] = useState<any[]>([]);
  const [payTotals, setPayTotals] = useState<Record<string, any>>({});
  const [payroll, setPayroll] = useState<any[]>([]);
  const [payrollTotals, setPayrollTotals] = useState<Record<string, any>>({});
  const [expenses, setExpenses] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [balances, setBalances] = useState<any[]>([]);
  const [transfers, setTransfers] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [pricing, setPricing] = useState<any>(null);

  // one-row-at-a-time inline editing for payments
  const [editingPay, setEditingPay] = useState<any | null>(null);
  // expanded payroll row → inline payout panel
  const [expandedPayroll, setExpandedPayroll] = useState<string | null>(null);
  // account operations modal (shared by Сводка balances)
  const [opsAccount, setOpsAccount] = useState<{ id: string; name: string } | null>(null);
  // «Должны мне» breakdown modal — currency of the card clicked (null = closed)
  const [receivablesCur, setReceivablesCur] = useState<string | null>(null);

  const loadAccounts = useCallback(async () => {
    try {
      const r = await financeApi.getAccounts();
      setAccounts(r.data?.data?.accounts || []);
    } catch { /* ignore */ }
  }, []);

  const loadBalances = useCallback(async () => {
    try {
      const r = await financeApi.accountBalances(period);
      setBalances(r.data?.data?.balances || []);
    } catch { /* ignore */ }
  }, [period]);

  const loadTransfers = useCallback(async () => {
    try {
      const r = await financeApi.getTransfers();
      setTransfers(r.data?.data?.transfers || []);
    } catch { /* ignore */ }
  }, []);

  const loadEmployees = useCallback(async () => {
    try {
      const r = await usersApi.getAll();
      const list = r.data?.data?.users || r.data?.data || r.data?.users || [];
      setEmployees(Array.isArray(list) ? list : []);
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'summary') {
        const [r, pr, er] = await Promise.all([
          financeApi.getSummary(period),
          financeApi.getPayments(period).catch(() => null),
          financeApi.getExpenses(period).catch(() => null),
        ]);
        setSummary(r.data?.data?.byCurrency || {});
        setPayments(pr?.data?.data?.payments || []);
        setExpenses(er?.data?.data?.expenses || []);
        loadBalances();
      } else if (tab === 'payments' || tab === 'ggboot') {
        // Same table, one service at a time: техподдержка или GGBoot.
        const r = await financeApi.getPayments(period, { kind: tab === 'ggboot' ? 'ggboot' : 'support' });
        setPayments(r.data?.data?.payments || []);
        setPayTotals(r.data?.data?.totalsByCurrency || {});
        loadAccounts();
      } else if (tab === 'payroll') {
        const r = await financeApi.getPayroll(period);
        setPayroll(r.data?.data?.payroll || []);
        setPayrollTotals(r.data?.data?.totalsByCurrency || {});
        loadAccounts();
      } else if (tab === 'expenses') {
        const r = await financeApi.getExpenses(period);
        setExpenses(r.data?.data?.expenses || []);
        loadAccounts();
      } else if (tab === 'accounts') {
        await loadAccounts();
        loadEmployees();
        loadBalances();
        loadTransfers();
      } else if (tab === 'analytics') {
        const r = await financeApi.pricingAnalysis(850, 'RUB');
        setPricing(r.data?.data || null);
      }
    } catch {
      toast.error('Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  }, [tab, period, loadAccounts, loadEmployees, loadBalances, loadTransfers]);

  useEffect(() => { load(); }, [load]);

  const generateMonth = async () => {
    try {
      const a = await financeApi.generatePayments(period);
      const b = await financeApi.generatePayroll(period);
      const c = await financeApi.generateExpenses(period);
      const p = a.data?.data?.created ?? 0;
      const pu = a.data?.data?.updated ?? 0;
      const s = b.data?.data?.created ?? 0;
      const ex = c.data?.data?.created ?? 0;
      toast.success(`Оплат: создано ${p}${pu ? `, обновлено ${pu}` : ''} · зарплат ${s} · расходов ${ex}`);
      load();
    } catch {
      toast.error('Ошибка генерации месяца');
    }
  };

  // ── payments ────────────────────────────────────────────────────────────────
  const startEdit = (p: any) => setEditingPay({
    id: p.id,
    amountDue: p.amountDue ?? 0,
    amountReceived: p.amountReceived ?? p.amountDue ?? 0,
    currency: p.currency || 'RUB',
    accountId: p.accountId || '',
    salaryCredit: p.salaryCredit ?? '',
    returnAccountId: p.returnAccountId || '',
    status: p.status || 'paid',
    comment: p.comment || '',
  });

  const saveEdit = async () => {
    if (!editingPay) return;
    try {
      await financeApi.updatePayment(editingPay.id, {
        amountDue: editingPay.amountDue,
        amountReceived: editingPay.amountReceived,
        currency: editingPay.currency,
        accountId: editingPay.accountId || null,
        salaryCredit: editingPay.salaryCredit === '' ? null : editingPay.salaryCredit,
        returnAccountId: editingPay.returnAccountId || null,
        status: editingPay.status,
        comment: editingPay.comment,
      });
      toast.success('Оплата отмечена');
      setEditingPay(null);
      load();
    } catch {
      toast.error('Не удалось сохранить');
    }
  };

  // ── payroll ─────────────────────────────────────────────────────────────────
  const revertPayroll = async (pr: any) => {
    if (!window.confirm('Отменить выплату? Записанные выплаты удалятся, баланс счетов вернётся.')) return;
    try {
      await financeApi.updatePayroll(pr.id, { status: 'to_pay' });
      setExpandedPayroll(null);
      load();
    } catch {
      toast.error('Не удалось отменить');
    }
  };
  const deletePayrollRow = async (pr: any) => {
    if (!window.confirm(`Удалить ${pr.employee} из списка зарплат? Все его выплаты удалятся, баланс счетов вернётся.`)) return;
    try {
      await financeApi.deletePayroll(pr.id);
      setExpandedPayroll(null);
      load();
    } catch {
      toast.error('Не удалось удалить');
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Финансы</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Оплаты клубов, зарплаты, расходы и счета за месяц</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center rounded-lg border border-border bg-card shadow-sm">
            <button onClick={() => setPeriod(shiftMonth(period, -1))} aria-label="Предыдущий месяц"
              className="h-9 w-9 grid place-items-center text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded-l-lg">
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <button onClick={() => setPeriod(currentMonthStr())} title="К текущему месяцу"
              className="h-9 min-w-[150px] px-3 text-sm font-medium text-foreground border-x border-border hover:bg-muted/60">
              {monthLabel(period)}
            </button>
            <button onClick={() => setPeriod(shiftMonth(period, 1))} aria-label="Следующий месяц"
              className="h-9 w-9 grid place-items-center text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded-r-lg">
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
          <Button variant="outline" size="sm" className="h-9" onClick={generateMonth}>Сгенерировать месяц</Button>
        </div>
      </div>

      {/* Tabs — segmented control */}
      <div className="inline-flex flex-wrap gap-1 rounded-lg bg-muted p-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3.5 py-1.5 text-sm rounded-md transition-colors ${
              tab === t.key
                ? 'bg-card text-foreground shadow-sm font-medium'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-muted-foreground">Загрузка…</div>}

      {/* ── Summary ── */}
      {tab === 'summary' && (
        <div className="space-y-4">
          <AccountOperationsModal
            accountId={opsAccount?.id || null}
            accountName={opsAccount?.name}
            initialPeriod={period}
            onClose={() => setOpsAccount(null)}
          />
          {receivablesCur !== null && (
            <ReceivablesModal
              currency={receivablesCur}
              initialPeriod={period}
              onClose={() => setReceivablesCur(null)}
            />
          )}
          {Object.keys(summary).length === 0 && !loading && (
            <div className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground text-center">
              Нет данных за {monthLabel(period)}. Нажмите «Сгенерировать месяц».
            </div>
          )}
          {Object.entries(summary).map(([c, s]: [string, any]) => (
            <SummaryCurrency key={c} currency={c} s={s}
              payments={payments.filter((p) => (p.currency || '') === c)}
              expenses={expenses.filter((e) => (e.currency || '') === c)}
              onDebtors={() => setReceivablesCur(c)}
              onTab={(t) => setTab(t)} />
          ))}
          <AccountBalances balances={balances} onOpen={(id, name) => setOpsAccount({ id, name })} />
          <RecurringBlock expenses={expenses} />
        </div>
      )}

      {/* ── Payments / GGBoot — одна таблица, разные услуги ── */}
      {(tab === 'payments' || tab === 'ggboot') && (
        <div className="space-y-3">
          {tab === 'ggboot' && (
            <div className="rounded-xl border border-border bg-card shadow-sm p-5">
              <div className="text-base font-semibold text-foreground leading-tight">GGBoot</div>
              <div className="text-sm text-muted-foreground">
                начисления за наш софт — отдельно от техподдержки. Включается в карточке клуба:
                «Биллинг» → GGBoot, там же ставка за ПК.
              </div>
            </div>
          )}
          <TotalsBar totals={payTotals} kind="payments" />
          <div className="rounded-xl border border-border bg-card shadow-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Клиент</TableHead>
                  <TableHead>К оплате</TableHead>
                  <TableHead>Получено</TableHead>
                  <TableHead>Счёт</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((p) => {
                  const ed = editingPay && editingPay.id === p.id ? editingPay : null;
                  return (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.client}{p.kind === 'ggboot' && <GgbootTag />}</TableCell>
                      <TableCell>
                        {ed ? (
                          <div className="flex flex-col gap-1">
                            <Input type="number" className="h-8 w-28" value={ed.amountDue}
                              onChange={(e) => setEditingPay({ ...ed, amountDue: e.target.value })} />
                            <select className={`${selectCls} w-28`} value={ed.currency}
                              onChange={(e) => setEditingPay({ ...ed, currency: e.target.value })}>
                              {CURRENCIES.map((x) => <option key={x} value={x}>{cur(x)} {x}</option>)}
                            </select>
                          </div>
                        ) : (
                          <>{fmt(p.amountDue)} {cur(p.currency)}</>
                        )}
                      </TableCell>
                      <TableCell>
                        {ed ? (
                          <Input
                            type="number"
                            className="h-8 w-28"
                            value={ed.amountReceived}
                            onChange={(e) => setEditingPay({ ...ed, amountReceived: e.target.value })}
                          />
                        ) : (
                          <>{fmt(p.amountReceived)} {cur(p.currency)}</>
                        )}
                      </TableCell>
                      <TableCell>
                        {ed ? (
                          <>
                            <select
                              className={selectCls}
                              value={ed.accountId}
                              onChange={(e) => setEditingPay({ ...ed, accountId: e.target.value })}
                            >
                              <option value="">— счёт —</option>
                              {accounts.map((a) => (
                                <option key={a.id} value={a.id}>{a.name} ({KIND_LABEL[a.kind] || a.kind})</option>
                              ))}
                            </select>
                            {accounts.find((a) => a.id === ed.accountId)?.kind === 'employee' && (() => {
                              const rem = (Number(ed.amountReceived) || 0) - (Number(ed.salaryCredit) || 0);
                              const partial = ed.salaryCredit !== '' && rem > 0;
                              return (
                                <>
                                  <Input type="number" className="h-8 w-full mt-1"
                                    placeholder="в счёт зп (по умолч. — всё)"
                                    value={ed.salaryCredit}
                                    onChange={(e) => setEditingPay({ ...ed, salaryCredit: e.target.value })} />
                                  {partial && (
                                    <>
                                      <select
                                        className={`${selectCls} w-full mt-1`}
                                        value={ed.returnAccountId}
                                        onChange={(e) => setEditingPay({ ...ed, returnAccountId: e.target.value })}
                                      >
                                        <option value="">остаток {fmt(rem)} → на счёт…</option>
                                        {accounts.filter((a) => a.id !== ed.accountId).map((a) => (
                                          <option key={a.id} value={a.id}>{a.name} ({KIND_LABEL[a.kind] || a.kind})</option>
                                        ))}
                                      </select>
                                      <div className="text-[11px] text-muted-foreground mt-0.5">
                                        остаток {fmt(rem)} {cur(p.currency)} вернётся на выбранный счёт
                                      </div>
                                    </>
                                  )}
                                </>
                              );
                            })()}
                          </>
                        ) : (
                          <span className="text-sm text-muted-foreground">{p.account || '—'}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {ed ? (
                          <select
                            className={selectCls}
                            value={ed.status}
                            onChange={(e) => setEditingPay({ ...ed, status: e.target.value })}
                          >
                            <option value="unpaid">Не оплачено</option>
                            <option value="partial">Частично</option>
                            <option value="paid">Оплачено</option>
                          </select>
                        ) : (
                          <StatusChip status={p.status} />
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {ed ? (
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" onClick={saveEdit}>Сохранить</Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingPay(null)}>Отмена</Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => startEdit(p)}>Отметить</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {payments.length === 0 && !loading && (
                  <TableRow><TableCell colSpan={6} className="text-muted-foreground">
                    {tab === 'ggboot'
                      ? 'Пока ни у одного клуба не включён GGBoot. Включите его в карточке клуба и нажмите «Сгенерировать месяц».'
                      : 'Нет строк. Нажмите «Сгенерировать месяц».'}
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* ── Payroll ── */}
      {tab === 'payroll' && (
        <div className="space-y-3">
          <TotalsBar totals={payrollTotals} kind="payroll" />
          <div className="rounded-xl border border-border bg-card shadow-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Сотрудник</TableHead>
                  <TableHead>Оклад</TableHead>
                  <TableHead>К доплате</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payroll.map((pr) => {
                  const expanded = expandedPayroll === pr.id;
                  const remaining = Number(pr.remainingToPay) || 0;
                  const paidTotal = Number(pr.paidTotal) || 0;
                  return (
                    <React.Fragment key={pr.id}>
                      <TableRow>
                        <TableCell className="font-medium">{pr.employee}</TableCell>
                        <TableCell>{fmt(pr.salaryAmount)} {cur(pr.currency)}</TableCell>
                        <TableCell className={`font-semibold ${remaining < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                          {fmt(remaining)} {cur(pr.currency)}
                          {paidTotal > 0 && (
                            <div className="text-xs font-normal text-muted-foreground">
                              выплачено {fmt(paidTotal)} из {fmt(pr.salaryAmount)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell><StatusChip status={pr.status} /></TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setExpandedPayroll(expanded ? null : pr.id)}
                            >
                              {expanded ? 'Скрыть' : (pr.status === 'paid' ? 'Выплаты' : (paidTotal > 0 ? 'Доплатить' : 'Выплатить'))}
                            </Button>
                            {(pr.status === 'paid' || paidTotal > 0) && (
                              <Button size="sm" variant="ghost" onClick={() => revertPayroll(pr)}>Отменить</Button>
                            )}
                            <Button size="sm" variant="ghost" className="text-red-600 dark:text-red-400"
                              onClick={() => deletePayrollRow(pr)}>Удалить</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {expanded && (
                        <TableRow>
                          <TableCell colSpan={5} className="p-0">
                            <PayrollPayPanel
                              pr={pr}
                              accounts={accounts}
                              onChanged={() => { load(); }}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
                {payroll.length === 0 && !loading && (
                  <TableRow><TableCell colSpan={5} className="text-muted-foreground">
                    Нет строк. Нажмите «Сгенерировать месяц».
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* ── Expenses ── */}
      {tab === 'expenses' && (
        <ExpensesTab
          period={period}
          expenses={expenses}
          accounts={accounts}
          onChanged={load}
        />
      )}

      {/* ── Accounts ── */}
      {tab === 'accounts' && (
        <AccountsTab
          accounts={accounts}
          employees={employees}
          balances={balances}
          transfers={transfers}
          period={period}
          onChanged={() => { loadAccounts(); loadBalances(); loadTransfers(); }}
        />
      )}

      {/* ── Analytics ── */}
      {tab === 'analytics' && (
        <div className="space-y-3">
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="text-sm text-muted-foreground">
              Активные ₽-клубы, которые платят меньше прайса {pricing?.benchmark ?? 850} ₽/ПК
            </div>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
              Теряем ≈ {fmt(pricing?.totalLostPerMonth || 0)} ₽/мес
            </div>
            <div className="text-xs text-muted-foreground">{pricing?.count || 0} клуб(ов) ниже прайса</div>
          </div>
          <div className="rounded-xl border border-border bg-card shadow-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Клуб</TableHead>
                  <TableHead>ПК</TableHead>
                  <TableHead>Тариф</TableHead>
                  <TableHead>₽/ПК</TableHead>
                  <TableHead>Недобор/ПК</TableHead>
                  <TableHead>Теряем/мес</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(pricing?.clubs || []).map((c: any) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>{c.pcCount}</TableCell>
                    <TableCell>{fmt(c.tariff)} ₽</TableCell>
                    <TableCell className="text-red-600 dark:text-red-400">{fmt(c.ratePerPc)} ₽</TableCell>
                    <TableCell className="text-amber-600 dark:text-amber-400">−{fmt(c.shortfallPerPc)} ₽</TableCell>
                    <TableCell className="font-semibold text-red-600 dark:text-red-400">{fmt(c.lostPerMonth)} ₽</TableCell>
                  </TableRow>
                ))}
                {(!pricing?.clubs || pricing.clubs.length === 0) && !loading && (
                  <TableRow><TableCell colSpan={6} className="text-muted-foreground">
                    Все активные ₽-клубы платят по прайсу (≥ {pricing?.benchmark ?? 850} ₽/ПК) — или тарифы/ПК ещё не заданы.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

    </div>
  );
};

// ── small presentational bits ────────────────────────────────────────────────
// ── Сводка: одна валюта = одна "полоса месяца" ───────────────────────────────
const pct = (part: number, whole: number) => (whole > 0 ? Math.min(100, Math.round((part / whole) * 100)) : 0);

const Kpi: React.FC<{ label: string; value: string; sub?: string; tone?: 'green' | 'red' | 'amber' | 'neutral'; onClick?: () => void }> =
  ({ label, value, sub, tone = 'neutral', onClick }) => {
    const toneCls = tone === 'green' ? 'text-green-600 dark:text-green-400'
      : tone === 'red' ? 'text-red-600 dark:text-red-400'
        : tone === 'amber' ? 'text-amber-600 dark:text-amber-400' : 'text-foreground';
    return (
      <div onClick={onClick}
        className={`rounded-xl bg-muted p-4 min-w-0 ${onClick ? 'cursor-pointer hover:bg-muted/70 transition-colors' : ''}`}>
        <div className="text-sm text-muted-foreground truncate">{label}{onClick ? ' ›' : ''}</div>
        <div className={`text-2xl font-semibold tabular-nums leading-tight mt-1 ${toneCls}`}>{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1.5 truncate">{sub}</div>}
      </div>
    );
  };

const SummaryCurrency: React.FC<{
  currency: string; s: any; payments: any[]; expenses: any[];
  onDebtors: () => void; onTab: (t: Tab) => void;
}> = ({ currency: c, s, payments, expenses, onDebtors, onTab }) => {
  const due = Number(s.clientsDue) || 0;
  const got = Number(s.clientsReceived) || 0;
  const receivable = Number(s.receivable) || 0;
  const payable = Number(s.payable) || 0;
  const net = got - (Number(s.payrollToPay) || 0) - (Number(s.expensesToPay) || 0);
  const collected = pct(got, due);
  const paidCount = payments.filter((p) => (Number(p.remaining) || 0) <= 0.001).length;
  const debtors = payments
    .filter((p) => (Number(p.remaining) || 0) > 0.001)
    .sort((a, b) => (Number(b.remaining) || 0) - (Number(a.remaining) || 0));
  const recurringSum = expenses.filter((e) => e.isRecurring).reduce((acc, e) => acc + (Number(e.amount) || 0), 0);
  const byKind = s.byKind || {};
  const gg = byKind.ggboot || { due: 0, received: 0 };
  const sup = byKind.support || { due: 0, received: 0 };
  const barTone = collected >= 90 ? 'bg-green-500' : collected >= 60 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm p-5 space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-primary text-lg font-semibold">{cur(c)}</span>
          <div>
            <div className="text-base font-semibold text-foreground leading-tight">{c === 'RUB' ? 'Рубли' : c === 'KZT' ? 'Тенге' : c}</div>
            <div className="text-sm text-muted-foreground">
              оплачено {paidCount} из {payments.length} начислений
              {Number(gg.due) > 0 && (
                <span> · поддержка {fmt(sup.received)} из {fmt(sup.due)} · GGBoot {fmt(gg.received)} из {fmt(gg.due)}</span>
              )}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-semibold tabular-nums ${collected >= 90 ? 'text-green-600 dark:text-green-400' : collected >= 60 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>{collected}%</div>
          <div className="text-xs text-muted-foreground">собрано {fmt(got)} из {fmt(due)}</div>
        </div>
      </div>

      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${barTone} transition-all`} style={{ width: `${collected}%` }} />
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <Kpi label="Должны мне" value={`${fmt(receivable)} ${cur(c)}`} tone="amber"
          sub={debtors.length ? `${debtors.length} клуб(ов)` : 'все оплатили'} onClick={onDebtors} />
        <Kpi label="Получено" value={`${fmt(got)} ${cur(c)}`} tone="green"
          sub={s.byAccount && Object.keys(s.byAccount).length
            ? Object.entries(s.byAccount).map(([a, v]: [string, any]) => `${a}: ${fmt(v)}`).join(' · ') : undefined} />
        <Kpi label="К выплате" value={`${fmt(payable)} ${cur(c)}`} tone="red"
          sub={`зарплаты ${fmt(s.payrollToPay)} · расходы ${fmt(s.expensesToPay)}`} onClick={() => onTab('payroll')} />
        <Kpi label="Итог месяца" value={`${net >= 0 ? '+' : ''}${fmt(net)} ${cur(c)}`} tone={net >= 0 ? 'green' : 'red'}
          sub={recurringSum ? `из них регулярных расходов ${fmt(recurringSum)}` : 'получено − к выплате'} />
      </div>

      {debtors.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-foreground">Кто ещё не оплатил</div>
            {debtors.length > 5 && (
              <button onClick={onDebtors} className="text-sm text-primary hover:underline">все {debtors.length} ›</button>
            )}
          </div>
          <div className="divide-y divide-border/70">
            {debtors.slice(0, 5).map((p) => {
              const part = (Number(p.amountReceived) || 0) > 0;
              return (
                <div key={p.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span className="truncate text-foreground">{p.client}{p.kind === 'ggboot' && <GgbootTag />}</span>
                  <span className="shrink-0 flex items-center gap-3">
                    {part && <span className="text-xs rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 px-2 py-0.5">частично</span>}
                    <span className="tabular-nums font-medium text-foreground min-w-[110px] text-right">{fmt(p.remaining)} {cur(c)}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const RecurringBlock: React.FC<{ expenses: any[] }> = ({ expenses }) => {
  const rec = expenses.filter((e) => e.isRecurring);
  if (!rec.length) return null;
  const byCur: Record<string, number> = {};
  for (const e of rec) byCur[e.currency || '—'] = (byCur[e.currency || '—'] || 0) + (Number(e.amount) || 0);
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm p-5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-muted"><ArrowPathIcon className="h-4 w-4 text-muted-foreground" /></span>
          <div>
            <div className="text-base font-semibold text-foreground leading-tight">Регулярные расходы</div>
            <div className="text-sm text-muted-foreground">повторяются каждый месяц</div>
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          {Object.entries(byCur).map(([c, v]) => <span key={c} className="ml-3 text-lg font-semibold tabular-nums text-foreground">{fmt(v)} {cur(c)}</span>)}
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {rec.map((e) => (
          <div key={e.id} className="flex items-center justify-between gap-2 text-sm rounded-lg bg-muted/50 px-3 py-2">
            <span className="truncate text-foreground">{e.title}
              <span className="ml-1.5 text-xs text-muted-foreground">{CATEGORY_LABEL[e.category] || e.category}</span>
            </span>
            <span className="shrink-0 flex items-center gap-2">
              <StatusChip status={e.status} />
              <span className="tabular-nums">{fmt(e.amount)} {cur(e.currency)}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const GgbootTag: React.FC = () => (
  <span className="ml-1.5 align-middle rounded bg-violet-500/15 text-violet-700 dark:text-violet-300 px-1.5 py-0.5 text-[11px]">GGBoot</span>
);

const RecurringBadge: React.FC = () => (
  <span className="ml-1.5 inline-flex items-center gap-1 align-middle rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground"
    title="Повторяется каждый месяц">
    <ArrowPathIcon className="h-3 w-3" /> ежемесячно
  </span>
);

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, string> = {
    paid: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    partial: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    unpaid: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    to_pay: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  };
  const label: Record<string, string> = {
    paid: 'Оплачено', partial: 'Частично', unpaid: 'Не оплачено', to_pay: 'К оплате',
  };
  return <span className={`inline-block rounded px-2 py-0.5 text-xs ${map[status] || 'bg-muted'}`}>{label[status] || status}</span>;
};

const TotalsBar: React.FC<{ totals: Record<string, any>; kind: 'payments' | 'payroll' }> = ({ totals, kind }) => {
  const entries = Object.entries(totals || {});
  if (entries.length === 0) return null;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {entries.flatMap(([c, t]: [string, any]) => kind === 'payments' ? [
        <Kpi key={c + 'r'} label={`Получено, ${c}`} value={`${fmt(t.received)} ${cur(c)}`} tone="green" />,
        <Kpi key={c + 'd'} label={`Долг клубов, ${c}`} value={`${fmt(t.remaining)} ${cur(c)}`} tone="amber" />,
      ] : [
        <Kpi key={c + 'p'} label={`К доплате, ${c}`} value={`${fmt(t.remaining)} ${cur(c)}`} tone="red"
          sub={`оклады ${fmt(t.salary)} · выплачено ${fmt(t.paid)}`} />,
      ])}
    </div>
  );
};

// ── Account operations modal (выписка по счёту) ──────────────────────────────
const AccountOperationsModal: React.FC<{ accountId: string | null; accountName?: string; initialPeriod?: string; onClose: () => void }> = ({ accountId, accountName, initialPeriod, onClose }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState<string>(initialPeriod || currentMonthStr());

  useEffect(() => { setMonth(initialPeriod || currentMonthStr()); }, [initialPeriod, accountId]);

  useEffect(() => {
    if (!accountId) { setData(null); return; }
    setLoading(true);
    financeApi.accountOperations(accountId, month)
      .then((r) => setData(r.data?.data || null))
      .catch(() => toast.error('Не удалось загрузить операции'))
      .finally(() => setLoading(false));
  }, [accountId, month]);

  if (!accountId) return null;
  const monthIn: Record<string, any> = data?.monthIn || {};
  const monthOut: Record<string, any> = data?.monthOut || {};
  const monthCurs = Array.from(new Set([...Object.keys(monthIn), ...Object.keys(monthOut)]));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="font-semibold text-foreground">Операции · {data?.account?.name || accountName || ''}</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-lg leading-none">✕</button>
        </div>

        {/* month filter */}
        <div className="flex items-center gap-2 px-4 py-2 border-b border-border">
          <Button variant="outline" size="sm" onClick={() => setMonth(shiftMonth(month, -1))}>‹</Button>
          <span className="min-w-[130px] text-center text-sm font-medium text-foreground">{monthLabel(month)}</span>
          <Button variant="outline" size="sm" onClick={() => setMonth(shiftMonth(month, 1))}>›</Button>
          <Button variant="secondary" size="sm" onClick={() => setMonth(currentMonthStr())}>Текущий</Button>
        </div>

        {/* balance for the selected month (resets monthly) + приход/расход */}
        <div className="px-4 py-3 border-b border-border space-y-2">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {monthCurs.length === 0 && <span className="text-sm text-muted-foreground">Баланс за месяц: 0</span>}
            {monthCurs.map((c) => {
              const net = (Number(monthIn[c] || 0)) - (Number(monthOut[c] || 0));
              return (
                <span key={c} className="text-sm">
                  Баланс за месяц: <span className={`font-bold ${net < 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>{fmt(net)} {cur(c === '' ? null : c)}</span>
                </span>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {monthCurs.map((c) => (
              <span key={c} className="text-muted-foreground">
                <span className="text-green-600 dark:text-green-400 font-medium">приход +{fmt(monthIn[c] || 0)}</span>{' · '}
                <span className="text-red-600 dark:text-red-400 font-medium">расход −{fmt(monthOut[c] || 0)}</span>{' '}
                {cur(c === '' ? null : c)}
              </span>
            ))}
          </div>
        </div>

        <div className="p-4 overflow-y-auto">
          {loading && <div className="text-sm text-muted-foreground">Загрузка…</div>}
          {data && !loading && (
            <div className="space-y-0.5">
              {(data.operations || []).map((o: any, i: number) => (
                <div key={i} className="flex items-center gap-2 text-sm border-b border-border/40 py-1">
                  <span className="text-muted-foreground w-24 shrink-0">{o.date || '—'}</span>
                  <span className="flex-1">{o.label}</span>
                  <span className={`shrink-0 font-medium ${o.amount < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {o.amount > 0 ? '+' : ''}{fmt(o.amount)} {cur(o.currency)}
                  </span>
                </div>
              ))}
              {(!data.operations || data.operations.length === 0) && (
                <div className="text-sm text-muted-foreground">За этот месяц операций нет.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── «Должны мне» breakdown (кто должен и сколько) ────────────────────────────
const ReceivablesModal: React.FC<{ currency: string; initialPeriod?: string; onClose: () => void }> = ({ currency, initialPeriod, onClose }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState<string>(initialPeriod || currentMonthStr());

  useEffect(() => {
    setLoading(true);
    financeApi.getPayments(month, { currency })
      .then((r) => {
        const ps = r.data?.data?.payments || [];
        setRows(ps
          .filter((p: any) => (Number(p.remaining) || 0) > 0.001)
          .sort((a: any, b: any) => (Number(b.remaining) || 0) - (Number(a.remaining) || 0)));
      })
      .catch(() => toast.error('Не удалось загрузить'))
      .finally(() => setLoading(false));
  }, [month, currency]);

  const total = rows.reduce((s, p) => s + (Number(p.remaining) || 0), 0);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="font-semibold text-foreground">Должны мне · {cur(currency)} {currency}</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-lg leading-none">✕</button>
        </div>

        <div className="flex items-center gap-2 px-4 py-2 border-b border-border">
          <Button variant="outline" size="sm" onClick={() => setMonth(shiftMonth(month, -1))}>‹</Button>
          <span className="min-w-[130px] text-center text-sm font-medium text-foreground">{monthLabel(month)}</span>
          <Button variant="outline" size="sm" onClick={() => setMonth(shiftMonth(month, 1))}>›</Button>
          <Button variant="secondary" size="sm" onClick={() => setMonth(currentMonthStr())}>Текущий</Button>
        </div>

        <div className="px-4 py-3 border-b border-border text-sm">
          Итого долг: <span className="font-bold text-amber-600 dark:text-amber-400">{fmt(total)} {cur(currency)}</span>
          <span className="text-muted-foreground"> · {rows.length} клиент(ов)</span>
        </div>

        <div className="p-4 overflow-y-auto">
          {loading && <div className="text-sm text-muted-foreground">Загрузка…</div>}
          {!loading && rows.map((p) => (
            <div key={p.id} className="flex items-center gap-2 text-sm border-b border-border/40 py-1.5">
              <span className="flex-1 font-medium truncate">{p.client}{p.kind === 'ggboot' && <GgbootTag />}</span>
              <span className="text-xs text-muted-foreground shrink-0">
                начислено {fmt(p.amountDue)} · получено {fmt(p.amountReceived)}
              </span>
              <span className="shrink-0 font-semibold text-amber-600 dark:text-amber-400 w-28 text-right">
                {fmt(p.remaining)} {cur(p.currency)}
              </span>
            </div>
          ))}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-muted-foreground">За этот месяц никто не должен — все оплатили 👌</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Account balances (текущие остатки по счетам) ─────────────────────────────
const AccountBalances: React.FC<{ balances: any[]; onOpen?: (id: string, name: string) => void }> = ({ balances, onOpen }) => {
  if (!balances || balances.length === 0) return null;
  const byCur: Record<string, any[]> = {};
  for (const b of balances) {
    const c = b.currency || '—';
    (byCur[c] = byCur[c] || []).push(b);
  }
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm p-5 space-y-4">
      <div>
        <div className="text-base font-semibold text-foreground leading-tight">Балансы по счетам</div>
        <div className="text-sm text-muted-foreground">движение денег за выбранный месяц · нажмите на счёт, чтобы открыть операции</div>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        {Object.entries(byCur).map(([c, list]) => {
          const total = list.reduce((s, b) => s + (Number(b.balance) || 0), 0);
          const maxAbs = Math.max(1, ...list.map((b) => Math.abs(Number(b.balance) || 0)));
          return (
            <div key={c} className="space-y-1">
              <div className="flex items-baseline justify-between pb-2 mb-1 border-b border-border">
                <span className="text-sm font-medium text-muted-foreground">{cur(c)} {c !== '—' ? c : ''}</span>
                <span className={`text-xl font-semibold tabular-nums ${total < 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>
                  {fmt(total)} {cur(c)}
                </span>
              </div>
              {list.map((b) => {
                const v = Number(b.balance) || 0;
                return (
                  <div key={b.accountId + c}
                    onClick={onOpen ? () => onOpen(b.accountId, b.name) : undefined}
                    className={`group grid grid-cols-[1fr_auto] gap-x-3 items-center py-1.5 ${onOpen ? 'cursor-pointer' : ''}`}
                    title={onOpen ? 'Операции по счёту' : undefined}>
                    <div className="min-w-0">
                      <div className="flex items-baseline gap-2 min-w-0">
                        <span className="text-sm text-foreground truncate group-hover:underline">{b.name}</span>
                        <span className="text-xs text-muted-foreground shrink-0">{KIND_LABEL[b.kind] || b.kind}</span>
                      </div>
                      <div className="mt-1 h-1 rounded-full bg-muted overflow-hidden">
                        <div className={`h-full ${v < 0 ? 'bg-red-400' : 'bg-primary/60'}`} style={{ width: `${Math.round((Math.abs(v) / maxAbs) * 100)}%` }} />
                      </div>
                    </div>
                    <span className={`text-sm tabular-nums font-medium ${v < 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>{fmt(v)}</span>
                  </div>
                );
              })}
            </div>
          );
        })}
        {Object.keys(byCur).length === 0 && <div className="text-sm text-muted-foreground">Пока нет операций за этот месяц.</div>}
      </div>
    </div>
  );
};

// ── Payroll payout panel («Выплатить»: сумма + счёт откуда) ───────────────────
const PayrollPayPanel: React.FC<{ pr: any; accounts: any[]; onChanged: () => void }> = ({ pr, accounts, onChanged }) => {
  const [amount, setAmount] = useState<string>((pr.remainingToPay > 0 ? String(pr.remainingToPay) : ''));
  const [accountId, setAccountId] = useState<string>('');
  const [busy, setBusy] = useState(false);

  const pay = async () => {
    if (!(Number(amount) > 0)) { toast.error('Введите сумму выплаты'); return; }
    if (!accountId) { toast.error('Выберите счёт, откуда платите'); return; }
    setBusy(true);
    try {
      await financeApi.payPayroll(pr.id, { amount, accountId });
      toast.success('Выплата зафиксирована');
      onChanged();
    } catch {
      toast.error('Не удалось зафиксировать');
      setBusy(false);
    }
  };
  const removePayout = async (payId: string) => {
    try { await financeApi.deletePayrollPayment(pr.id, payId); onChanged(); }
    catch { toast.error('Ошибка'); }
  };

  const payouts: any[] = Array.isArray(pr.payments) ? pr.payments : [];
  return (
    <div className="space-y-2 p-3 bg-muted/40 border-t border-border">
      {payouts.length > 0 && (
        <div className="space-y-1">
          {payouts.map((p) => (
            <div key={p.id} className="flex items-center gap-2 text-sm">
              <span className="font-medium">{fmt(p.amount)} {cur(p.currency)}</span>
              <span className="text-muted-foreground">
                со счёта «{p.account || '—'}»{p.paidAt ? ` · ${p.paidAt}` : ''}
              </span>
              <button className="text-xs text-red-600 dark:text-red-400 underline hover:opacity-80"
                onClick={() => removePayout(p.id)}>удалить</button>
            </div>
          ))}
          <div className="text-xs text-muted-foreground">
            выплачено {fmt(pr.paidTotal)} из {fmt(pr.salaryAmount)} {cur(pr.currency)}
            {pr.remainingToPay > 0 ? ` · остаток ${fmt(pr.remainingToPay)}` : ' · полностью'}
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Input type="number" className="h-8 w-36" placeholder="сумма выплаты"
          value={amount} onChange={(e) => setAmount(e.target.value)} />
        <span className="text-sm text-muted-foreground">со счёта:</span>
        <select className={selectCls} value={accountId} onChange={(e) => setAccountId(e.target.value)}>
          <option value="">— счёт —</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({KIND_LABEL[a.kind] || a.kind})</option>)}
        </select>
        <Button size="sm" onClick={pay} disabled={busy}>Зафиксировать</Button>
      </div>
    </div>
  );
};

// ── Expenses tab ─────────────────────────────────────────────────────────────
const ExpensesTab: React.FC<{ period: string; expenses: any[]; accounts: any[]; onChanged: () => void }> = ({ period, expenses, accounts, onChanged }) => {
  const [form, setForm] = useState<any>({ title: '', amount: '', currency: 'RUB', category: 'rent', accountId: '', isRecurring: false });

  const add = async () => {
    if (!form.title.trim()) { toast.error('Введите название'); return; }
    try {
      await financeApi.createExpense({ ...form, accountId: form.accountId || null, period });
      toast.success('Расход добавлен');
      setForm({ title: '', amount: '', currency: 'RUB', category: 'rent', accountId: '', isRecurring: false });
      onChanged();
    } catch { toast.error('Не удалось добавить'); }
  };
  const togglePaid = async (e: any) => {
    try { await financeApi.updateExpense(e.id, { status: e.status === 'paid' ? 'to_pay' : 'paid' }); onChanged(); }
    catch { toast.error('Ошибка'); }
  };
  const remove = async (e: any) => {
    if (!window.confirm('Удалить расход?')) return;
    try { await financeApi.deleteExpense(e.id); onChanged(); } catch { toast.error('Ошибка'); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card p-3 flex flex-wrap items-end gap-2">
        <Input placeholder="Название (аренда, сервис…)" className="w-56" value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Input placeholder="Сумма" type="number" className="w-32" value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        <select className={selectCls} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
          {CURRENCIES.map((c) => <option key={c} value={c}>{cur(c)} {c}</option>)}
        </select>
        <select className={selectCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
          {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className={selectCls} value={form.accountId} onChange={(e) => setForm({ ...form, accountId: e.target.value })}>
          <option value="">— счёт —</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <label className="flex items-center gap-1 text-sm text-foreground whitespace-nowrap">
          <input type="checkbox" checked={form.isRecurring}
            onChange={(e) => setForm({ ...form, isRecurring: e.target.checked })} />
          <ArrowPathIcon className="h-4 w-4 text-muted-foreground" /> ежемесячный
        </label>
        <Button size="sm" onClick={add}>Добавить</Button>
      </div>

      <div className="rounded-xl border border-border bg-card shadow-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Название</TableHead>
              <TableHead>Сумма</TableHead>
              <TableHead>Категория</TableHead>
              <TableHead>Счёт</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {expenses.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="font-medium">{e.title}{e.isRecurring ? <RecurringBadge /> : null}</TableCell>
                <TableCell>{fmt(e.amount)} {cur(e.currency)}</TableCell>
                <TableCell className="text-muted-foreground">{CATEGORY_LABEL[e.category] || e.category}</TableCell>
                <TableCell className="text-muted-foreground">{e.account || '—'}</TableCell>
                <TableCell><StatusChip status={e.status} /></TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="outline" onClick={() => togglePaid(e)}>
                      {e.status === 'paid' ? 'Отменить' : 'Оплачено'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(e)}>Удалить</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {expenses.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-muted-foreground">Расходов пока нет.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ── Accounts tab ─────────────────────────────────────────────────────────────
// ── Transfer between accounts (перевод между счетами) ────────────────────────
const TransferPanel: React.FC<{ accounts: any[]; transfers: any[]; onChanged: () => void }> = ({ accounts, transfers, onChanged }) => {
  const [form, setForm] = useState<any>({ fromAccountId: '', toAccountId: '', amount: '', currency: 'RUB', note: '' });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.fromAccountId || !form.toAccountId) { toast.error('Выберите оба счёта'); return; }
    if (form.fromAccountId === form.toAccountId) { toast.error('Счета должны отличаться'); return; }
    if (!(Number(form.amount) > 0)) { toast.error('Введите сумму'); return; }
    setBusy(true);
    try {
      await financeApi.createTransfer(form);
      toast.success('Перевод выполнен');
      setForm({ fromAccountId: '', toAccountId: '', amount: '', currency: form.currency, note: '' });
      onChanged();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Не удалось перевести');
    } finally { setBusy(false); }
  };
  const remove = async (t: any) => {
    if (!window.confirm('Удалить перевод? Балансы вернутся.')) return;
    try { await financeApi.deleteTransfer(t.id); onChanged(); } catch { toast.error('Ошибка'); }
  };

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm p-5 space-y-4">
      <div>
        <div className="text-base font-semibold text-foreground leading-tight">Перевод между счетами</div>
        <div className="text-sm text-muted-foreground">в одной валюте, балансы пересчитаются сразу</div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_auto_1fr]">
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Откуда</span>
          <select className={`${selectCls} w-full`} value={form.fromAccountId}
            onChange={(e) => setForm({ ...form, fromAccountId: e.target.value })}>
            <option value="">выберите счёт</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} · {KIND_LABEL[a.kind] || a.kind}</option>)}
          </select>
        </label>
        <div className="hidden lg:grid place-items-end pb-2 text-muted-foreground"><ArrowRightIcon className="h-5 w-5" /></div>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Куда</span>
          <select className={`${selectCls} w-full`} value={form.toAccountId}
            onChange={(e) => setForm({ ...form, toAccountId: e.target.value })}>
            <option value="">выберите счёт</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} · {KIND_LABEL[a.kind] || a.kind}</option>)}
          </select>
        </label>
      </div>
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-[130px_110px_minmax(180px,1fr)_auto] items-end">
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Сумма</span>
          <Input type="number" className="h-9" placeholder="0" value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Валюта</span>
          <select className={`${selectCls} w-full`} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{cur(c)} {c}</option>)}
          </select>
        </label>
        <label className="space-y-1 col-span-2 sm:col-span-1">
          <span className="text-xs text-muted-foreground">Комментарий</span>
          <Input placeholder="необязательно" className="h-9" value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </label>
        <Button className="h-9 col-span-2 sm:col-span-1" onClick={submit} disabled={busy}>Перевести</Button>
      </div>

      {transfers.length > 0 && (
        <div className="pt-2">
          <div className="text-sm font-medium text-foreground mb-1">Последние переводы</div>
          <div className="divide-y divide-border/70">
            {transfers.slice(0, 8).map((t) => (
              <div key={t.id} className="grid grid-cols-[90px_1fr_auto_auto] items-center gap-3 py-2 text-sm">
                <span className="text-muted-foreground tabular-nums">{t.date || ''}</span>
                <span className="text-foreground truncate">{t.fromAccount} <span className="text-muted-foreground">→</span> {t.toAccount}
                  {t.note ? <span className="text-muted-foreground"> · {t.note}</span> : null}</span>
                <span className="font-medium tabular-nums">{fmt(t.amount)} {cur(t.currency)}</span>
                <button className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400" title="Удалить перевод"
                  onClick={() => remove(t)}><TrashIcon className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AccountsTab: React.FC<{ accounts: any[]; employees: any[]; balances: any[]; transfers: any[]; period: string; onChanged: () => void }> = ({ accounts, employees, balances, transfers, period, onChanged }) => {
  const [form, setForm] = useState<any>({ name: '', kind: 'settlement', employeeId: '', currency: '' });
  const [opsAccount, setOpsAccount] = useState<{ id: string; name: string } | null>(null);

  const add = async () => {
    if (!form.name.trim()) { toast.error('Введите название счёта'); return; }
    try {
      await financeApi.createAccount({
        name: form.name, kind: form.kind,
        employeeId: form.kind === 'employee' ? (form.employeeId || null) : null,
        currency: form.currency || '',
      });
      toast.success('Счёт создан');
      setForm({ name: '', kind: 'settlement', employeeId: '', currency: '' });
      onChanged();
    } catch { toast.error('Не удалось создать счёт'); }
  };
  const toggleActive = async (a: any) => {
    try { await financeApi.updateAccount(a.id, { isActive: !a.isActive }); onChanged(); }
    catch { toast.error('Ошибка'); }
  };
  const remove = async (a: any) => {
    if (!window.confirm('Удалить счёт?')) return;
    try { await financeApi.deleteAccount(a.id); onChanged(); } catch { toast.error('Ошибка'); }
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-5 xl:grid-cols-[3fr_2fr] items-start">
        <AccountBalances balances={balances} onOpen={(id, name) => setOpsAccount({ id, name })} />
        <TransferPanel accounts={accounts} transfers={transfers} onChanged={onChanged} />
      </div>

      <AccountOperationsModal
        accountId={opsAccount?.id || null}
        accountName={opsAccount?.name}
        initialPeriod={period}
        onClose={() => setOpsAccount(null)}
      />

      <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-border">
          <div>
            <div className="text-base font-semibold text-foreground leading-tight">Счета</div>
            <div className="text-sm text-muted-foreground">куда приходят деньги и откуда платим</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input placeholder="Название нового счёта" className="h-9 w-52" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <select className={selectCls} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {form.kind === 'employee' && (
              <select className={selectCls} value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })}>
                <option value="">сотрудник</option>
                {employees.map((u) => (
                  <option key={u.id} value={u.id}>{u.firstName || ''} {u.lastName || ''} {u.username ? `(${u.username})` : ''}</option>
                ))}
              </select>
            )}
            <select className={selectCls} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
              <option value="">любая валюта</option>
              {CURRENCIES.map((c) => <option key={c} value={c}>{cur(c)} {c}</option>)}
            </select>
            <Button variant="outline" className="h-9" onClick={add}>Добавить</Button>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Название</TableHead>
              <TableHead>Тип</TableHead>
              <TableHead>Сотрудник</TableHead>
              <TableHead>Валюта</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {accounts.map((a) => {
              const emp = employees.find((u) => u.id === a.employeeId);
              return (
                <TableRow key={a.id}>
                  <TableCell className="font-medium">
                    <button className="hover:underline text-left" title="Операции по счёту"
                      onClick={() => setOpsAccount({ id: a.id, name: a.name })}>{a.name}</button>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{KIND_LABEL[a.kind] || a.kind}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {a.employeeId ? (emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : '✓') : '—'}
                  </TableCell>
                  <TableCell>{a.currency ? `${cur(a.currency)} ${a.currency}` : '—'}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1.5 text-xs ${a.isActive ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground'}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${a.isActive ? 'bg-green-500' : 'bg-muted-foreground/50'}`} />
                      {a.isActive ? 'активен' : 'выключен'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setOpsAccount({ id: a.id, name: a.name })}>Операции</Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleActive(a)}>
                        {a.isActive ? 'Выключить' : 'Включить'}
                      </Button>
                      <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-600" onClick={() => remove(a)}><TrashIcon className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {accounts.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-muted-foreground">Счетов пока нет — создайте первый.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ── Clients billing tab (тарифы клиентов) ─────────────────────────────────────
const ClientsBillingTab: React.FC<{ clients: any[]; onChanged: () => void }> = ({ clients, onChanged }) => {
  const [edit, setEdit] = useState<any | null>(null);

  const start = (c: any) => setEdit({
    id: c.id,
    pcCount: c.pcCount ?? '',
    ratePerPc: c.ratePerPc ?? '',
    tariff: c.tariff ?? '',
    currency: c.currency || 'RUB',
    billingStatus: c.billingStatus || 'new',
  });

  const calcTariff = () => {
    const pc = Number(edit.pcCount) || 0;
    const r = Number(edit.ratePerPc) || 0;
    setEdit({ ...edit, tariff: pc * r });
  };

  const save = async () => {
    try {
      await clientsApi.update(edit.id, {
        pcCount: edit.pcCount === '' ? null : Number(edit.pcCount),
        ratePerPc: edit.ratePerPc === '' ? null : edit.ratePerPc,
        tariff: edit.tariff === '' ? null : edit.tariff,
        currency: edit.currency,
        billingStatus: edit.billingStatus,
      });
      toast.success('Клиент обновлён');
      setEdit(null);
      onChanged();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Не удалось сохранить');
    }
  };

  const STATUS_LABEL: Record<string, string> = { new: 'Новый', active: 'Активен', paused: 'Пауза', lost: 'Ушёл' };

  return (
    <div className="space-y-3">
      <div className="text-xs text-muted-foreground">
        Тариф в месяц = ставка за ПК × количество ПК (кнопка «=»). Для спец-условий впишите тариф вручную.
        Активировать клиента можно только с заданными тарифом и валютой.
      </div>
      <div className="rounded-xl border border-border bg-card shadow-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Клуб</TableHead>
              <TableHead>ПК</TableHead>
              <TableHead>Ставка/ПК</TableHead>
              <TableHead>Тариф/мес</TableHead>
              <TableHead>Валюта</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clients.map((c) => {
              const ed = edit && edit.id === c.id ? edit : null;
              return (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.displayName || c.name || c.telegramChatTitle}</TableCell>
                  <TableCell>
                    {ed ? (
                      <Input type="number" className="h-8 w-20" value={ed.pcCount}
                        onChange={(e) => setEdit({ ...ed, pcCount: e.target.value })} />
                    ) : (c.pcCount ?? '—')}
                  </TableCell>
                  <TableCell>
                    {ed ? (
                      <Input type="number" className="h-8 w-24" value={ed.ratePerPc}
                        onChange={(e) => setEdit({ ...ed, ratePerPc: e.target.value })} />
                    ) : (c.ratePerPc != null ? fmt(c.ratePerPc) : '—')}
                  </TableCell>
                  <TableCell>
                    {ed ? (
                      <div className="flex items-center gap-1">
                        <Input type="number" className="h-8 w-28" value={ed.tariff}
                          onChange={(e) => setEdit({ ...ed, tariff: e.target.value })} />
                        <Button size="sm" variant="ghost" title="= ставка × ПК" onClick={calcTariff}>=</Button>
                      </div>
                    ) : (c.tariff != null ? `${fmt(c.tariff)} ${cur(c.currency)}` : '—')}
                  </TableCell>
                  <TableCell>
                    {ed ? (
                      <select className={selectCls} value={ed.currency}
                        onChange={(e) => setEdit({ ...ed, currency: e.target.value })}>
                        {CURRENCIES.map((x) => <option key={x} value={x}>{cur(x)} {x}</option>)}
                      </select>
                    ) : (c.currency ? `${cur(c.currency)} ${c.currency}` : '—')}
                  </TableCell>
                  <TableCell>
                    {ed ? (
                      <select className={selectCls} value={ed.billingStatus}
                        onChange={(e) => setEdit({ ...ed, billingStatus: e.target.value })}>
                        <option value="new">Новый</option>
                        <option value="active">Активен</option>
                        <option value="paused">Пауза</option>
                        <option value="lost">Ушёл</option>
                      </select>
                    ) : (
                      <span className={`inline-block rounded px-2 py-0.5 text-xs ${
                        c.billingStatus === 'active'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                          : c.billingStatus === 'new'
                            ? 'bg-muted text-muted-foreground'
                            : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                      }`}>{STATUS_LABEL[c.billingStatus] || c.billingStatus}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {ed ? (
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" onClick={save}>Сохранить</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEdit(null)}>Отмена</Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => start(c)}>Изменить</Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {clients.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-muted-foreground">
                Клиентов пока нет — появятся автоматически из чатов Telegram (или запустите backfill_clients).
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default FinancePage;
